import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang, pick } from "@/contexts/LanguageContext";
import SeoHead from "@/components/SeoHead";
import { ROUTE_KEYS } from "@/i18n/routeMap";

const KEYS = ["rental_conditions", "terms", "privacy", "cookies"];
const KEY_TO_ROUTE = { rental_conditions: ROUTE_KEYS.RENTAL_CONDITIONS, terms: ROUTE_KEYS.TERMS, privacy: ROUTE_KEYS.PRIVACY, cookies: ROUTE_KEYS.COOKIES };
const KEY_TO_API = {
  rental_conditions: "rental-conditions",
  terms: "terms-and-conditions",
  privacy: "privacy-policy",
  cookies: "cookies-policy",
};

export default function LegalPage({ type }) {
  const { lang, t } = useLang();
  const [page, setPage] = useState(null);
  const valid = KEYS.includes(type);
  const apiKey = valid ? KEY_TO_API[type] : null;

  useEffect(() => {
    if (!apiKey) return;
    (async () => {
      try {
        const { data } = await api.get(`/legal-pages/${apiKey}`);
        setPage(data);
        const seoTitle = pick(data?.seoTitle, lang) || pick(data?.title, lang);
        if (seoTitle) document.title = seoTitle;
        const meta = pick(data?.metaDescription, lang);
        let el = document.querySelector('meta[name="description"]');
        if (!el) { el = document.createElement("meta"); el.name = "description"; document.head.appendChild(el); }
        el.content = meta || "";
      } catch {
        setPage({});
      }
    })();
  }, [apiKey, lang]);

  if (!valid) return null;

  const title = pick(page?.title, lang) || t.legal[type];
  const content = pick(page?.content, lang);
  const description = pick(page?.metaDescription, lang);
  const readyLocales = Object.entries(page?.localeStatus || {}).filter(([, status]) => status === "READY").map(([locale]) => locale);
  const legalJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    inLanguage: lang,
  };

  return (
    <div className="bg-[#0C0C0C] pt-32 md:pt-40 pb-24 min-h-screen" data-testid={`legal-page-${type}`}>
      <SeoHead title={pick(page?.seoTitle, lang) || title} description={description} lang={lang}
        pageKey={KEY_TO_ROUTE[type]} readyLocales={readyLocales} jsonLd={legalJsonLd}
        noindex={!page?.active || page?.pendingReview} />
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <div className="text-overline text-[#C9A55A] mb-4">{t.legal.kicker}</div>
        <h1 className="font-display text-[#F5F1EB] text-4xl md:text-5xl">{title}</h1>
        {page?.pendingReview && (
          <div className="mt-6 inline-block text-[10px] tracking-[0.2em] uppercase border border-[#C9A55A]/40 text-[#C9A55A] px-2 py-1">
            {t.legal.pendingReview}
          </div>
        )}
        <div className="mt-10 text-[#F5F1EB]/80 font-light leading-relaxed whitespace-pre-line">
          {content || "—"}
        </div>
      </div>
    </div>
  );
}
